// client/src/hooks/useChatMessages.js

import { useState, useCallback, useMemo } from 'react'
import useAuth from './useAuth'
import ApiChat from '../api/ApiChat'
import ChatService from '../services/ChatService'
import {
  getCachedMessagePage,
  setCachedMessagePage,
} from '../utils/messageCacheUtils'

/**
 * Custom hook for managing chat messages and operations
 * @param {Object} activeChannel - Currently active channel
 * @param {boolean} chatConnected - Whether chat service is connected
 * @returns {Object} Messages state and operations
 */
export default function useChatMessages(activeChannel, chatConnected) {
  const { user } = useAuth()
  const [messages, setMessages] = useState([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [pendingMessages, setPendingMessages] = useState(new Map()) // tempId -> { message, status }
  const [nextCursor, setNextCursor] = useState(null)

  const getResponseMessages = (response) => {
    if (Array.isArray(response?.items)) {
      return response.items
    }

    if (Array.isArray(response?.messages)) {
      return response.messages
    }

    return []
  }

  // Generate temporary ID for pending messages
  const generateTempId = () => `temp_${Date.now()}_${Math.random()}`

  const loadMessages = useCallback(async (channel) => {
    if (!channel) return

    try {
      setLoadingMessages(true)

      // Try to get cached messages first
      const cachedPage = getCachedMessagePage(channel.id)
      const cachedMessages = cachedPage?.messages ?? []
      if (cachedMessages.length > 0) {
        console.log(
          `Loaded ${cachedMessages.length} cached messages for channel ${channel.id}`,
        )
        setMessages(cachedMessages)
        setNextCursor(cachedPage?.nextCursor ?? null)
        setPendingMessages(new Map()) // Clear pending messages for new channel
        setLoadingMessages(false)
        return // Use cached data and skip API call
      }

      // No cache or empty cache, fetch from API
      const response = await ApiChat.getMessages(channel.id)
      const messages = getResponseMessages(response)
      const pageNextCursor = response?.pagination?.nextCursor ?? null

      setMessages(messages)
      setNextCursor(pageNextCursor)
      setPendingMessages(new Map()) // Clear pending messages for new channel

      // Cache the fetched messages
      setCachedMessagePage(channel.id, {
        messages,
        nextCursor: pageNextCursor,
      })
    } catch (error) {
      console.error('Failed to load messages:', error)
      throw new Error('Failed to load messages')
    } finally {
      setLoadingMessages(false)
    }
  }, [])

  const addMessage = useCallback(
    (message, action = 'new') => {
      // Check if this message matches a pending message (from our own send)
      if (action === 'new') {
        setPendingMessages((prev) => {
          const newPending = new Map(prev)
          // Look for pending messages that match this real message
          for (const [tempId, pendingData] of newPending.entries()) {
            if (
              pendingData.message.body === message.body &&
              pendingData.message.channel_id === message.channel_id &&
              pendingData.message.sender_player_id ===
                message.sender_player_id &&
              Math.abs(
                new Date(message.created_at) -
                  new Date(pendingData.message.created_at),
              ) < 10000
            ) {
              // This looks like our pending message - remove it
              newPending.delete(tempId)
              break
            }
          }
          return newPending
        })
      }

      setMessages((prev) => {
        // For delete actions, remove regardless of channel_id presence on payload
        if (action === 'delete') {
          const updatedMessages = prev.filter((m) => m.id !== message.id)
          // Update cache after deletion
          if (activeChannel) {
            setCachedMessagePage(activeChannel.id, {
              messages: updatedMessages,
              nextCursor,
            })
          }
          return updatedMessages
        }

        // For new/edit actions, ensure it belongs to the active channel
        if (!activeChannel || message.channel_id !== activeChannel.id) {
          return prev
        }

        let updatedMessages
        if (action === 'edit') {
          // Update existing message
          updatedMessages = prev.map((m) =>
            m.id === message.id
              ? { ...message, delivery_status: 'delivered' }
              : m,
          )
        } else {
          // Add new message (avoid duplicates)
          if (prev.find((m) => m.id === message.id)) {
            return prev
          }
          updatedMessages = [
            ...prev,
            { ...message, delivery_status: 'delivered' },
          ]
        }

        // Update cache with new messages
        setCachedMessagePage(activeChannel.id, {
          messages: updatedMessages,
          nextCursor,
        })
        return updatedMessages
      })
    },
    [activeChannel, nextCursor],
  )

  const editMessage = useCallback(
    async (messageId, newBody) => {
      if (!activeChannel || !chatConnected) {
        throw new Error('Not connected to chat')
      }

      try {
        // Try WebSocket first for real-time experience
        await ChatService.editMessage(messageId, newBody)
      } catch (error) {
        // Fallback to REST API
        console.warn('WebSocket failed, falling back to REST API:', error)
        try {
          const response = await ApiChat.editMessage(messageId, newBody)
          // Update message in local state if WebSocket didn't handle it
          setMessages((prev) =>
            prev.map((m) => (m.id === messageId ? response.message : m)),
          )
        } catch (apiError) {
          console.error('Failed to edit message via API:', apiError)
          throw apiError
        }
      }
    },
    [activeChannel, chatConnected],
  )

  const deleteMessage = useCallback(
    async (messageId) => {
      if (!activeChannel || !chatConnected) {
        throw new Error('Not connected to chat')
      }

      try {
        // Try WebSocket first for real-time experience
        await ChatService.deleteMessage(messageId)
      } catch (error) {
        // Fallback to REST API
        console.warn('WebSocket failed, falling back to REST API:', error)
        try {
          await ApiChat.deleteMessage(messageId)
          // Remove message from local state if WebSocket didn't handle it
          setMessages((prev) => prev.filter((m) => m.id !== messageId))
        } catch (apiError) {
          console.error('Failed to delete message via API:', apiError)
          throw apiError
        }
      }
    },
    [activeChannel, chatConnected],
  )

  const clearMessages = useCallback(() => {
    setMessages([])
    setNextCursor(null)
  }, [])

  // Load more (older) messages for infinite scroll
  const loadMoreMessages = useCallback(
    async (channel) => {
      if (!channel || loadingMessages) return false

      try {
        if (!nextCursor) return false

        setLoadingMessages(true)
        const response = await ApiChat.getMessages(channel.id, nextCursor, 50)

        const olderMessages = getResponseMessages(response)
        const updatedNextCursor = response?.pagination?.nextCursor ?? null

        // If no older messages, we've reached the beginning
        if (olderMessages.length === 0) {
          setNextCursor(updatedNextCursor)
          return false
        }

        // Prepend older messages to the current list
        setMessages((prev) => [...olderMessages, ...prev])
        setNextCursor(updatedNextCursor)

        // Update cache with the expanded message list
        const updatedMessages = [...olderMessages, ...messages]
        setCachedMessagePage(channel.id, {
          messages: updatedMessages,
          nextCursor: updatedNextCursor,
        })

        return true // Indicate more messages were loaded
      } catch (error) {
        console.error('Failed to load more messages:', error)
        return false
      } finally {
        setLoadingMessages(false)
      }
    },
    [loadingMessages, messages, nextCursor],
  )

  const sendMessage = useCallback(
    async (body) => {
      if (!activeChannel || !chatConnected) {
        throw new Error('Not connected to chat')
      }

      const tempId = generateTempId()
      const tempMessage = {
        id: tempId,
        body,
        channel_id: activeChannel.id,
        created_at: new Date().toISOString(),
        // Include sender_player_id to help robust matching when the real message returns
        sender_player_id: user?.primary_player_id ?? null,
        firstname: 'You',
        lastname: '',
        delivery_status: 'sending',
      }

      // Add temporary message to pending state
      setPendingMessages((prev) =>
        new Map(prev).set(tempId, {
          message: tempMessage,
          status: 'sending',
        }),
      )

      try {
        // Try WebSocket first for real-time experience
        await ChatService.sendMessage(activeChannel.id, body)

        // Update status to sent when WebSocket succeeds
        setPendingMessages((prev) => {
          const newPending = new Map(prev)
          if (newPending.has(tempId)) {
            const pendingMsg = newPending.get(tempId)
            newPending.set(tempId, {
              ...pendingMsg,
              status: 'sent',
            })
          }
          return newPending
        })

        // Status will be updated to 'delivered' when we receive the message back via WebSocket
      } catch (error) {
        // Fallback to REST API
        console.warn('WebSocket failed, falling back to REST API:', error)
        try {
          const response = await ApiChat.sendMessage(activeChannel.id, body)

          // Remove from pending and add to messages
          setPendingMessages((prev) => {
            const newPending = new Map(prev)
            newPending.delete(tempId)
            return newPending
          })

          // Add message to local state with delivered status since API worked
          setMessages((prev) => [
            ...prev,
            {
              ...response.message,
              delivery_status: 'delivered',
            },
          ])
        } catch (apiError) {
          // Mark as failed
          setPendingMessages((prev) => {
            const newPending = new Map(prev)
            if (newPending.has(tempId)) {
              const pendingMsg = newPending.get(tempId)
              newPending.set(tempId, {
                ...pendingMsg,
                status: 'failed',
              })
            }
            return newPending
          })

          console.error('Failed to send message via API:', apiError)
          throw apiError
        }
      }
    },
    [activeChannel, chatConnected, user?.primary_player_id],
  )

  // Combine regular messages with pending messages for display
  const allMessages = useMemo(() => {
    const pendingArray = Array.from(pendingMessages.values()).map((data) => ({
      ...data.message,
      delivery_status: data.status,
      isPending: true,
    }))

    return [...messages, ...pendingArray].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    )
  }, [messages, pendingMessages])

  return {
    messages: allMessages,
    loadingMessages,
    loadMessages,
    loadMoreMessages,
    addMessage,
    clearMessages,
    sendMessage,
    editMessage,
    deleteMessage,
  }
}
